import { useSelector } from "react-redux";
import { Spinner } from "@fluentui/react/lib/Spinner";
import { Stack } from "@fluentui/react/lib/Stack";

import { Card } from "Components/Generic/Card";
import { IWithThemeProps } from "Components/Generic/Theme/IWithThemeProps";
import { WithTheme } from "Components/Generic/Theme/WithTheme";
import { EntityType } from "Model/EntityType";
import { ICompany } from "Model/Storage/ICompany";
import { IItem } from "Model/Storage/IItem";
import { IProject } from "Model/Storage/IProject";
import { selectActiveItem, selectActiveItemType, selectIsLoadingExtended } from "Store/Selectors/Projects";
import { hasNoElements } from "Utils/Array";
import { sortItemByBooleanDesc } from "Utils/Item";
import { CompanyTile } from "./CompanyTile";
import { ContactTile } from "./ContactTile";
import { useParticipantStyles } from "./UseParticipantsStyles";

export const Participants = WithTheme(({ theme: { spacing } }: IWithThemeProps): JSX.Element => {
  const type = useSelector(selectActiveItemType);
  const item = useSelector(selectActiveItem);
  const isLoadingExtended = useSelector(selectIsLoadingExtended);
  const { cardStyles } = useParticipantStyles();

  if (![EntityType.Project, EntityType.Company].includes(type)) {
    return null;
  }

  const itemList = getListByType(type, item);
  const Tile = getTileByType(type);

  const showSpinner = hasNoElements(itemList) && isLoadingExtended;

  return (
    <Card styleSet={cardStyles}>
      {showSpinner
        ? (
          <Stack horizontal horizontalAlign="center" verticalFill verticalAlign="center">
            <Stack.Item grow>
              <Spinner />
            </Stack.Item>
          </Stack>
        )
        : (
          <Stack horizontal tokens={{ childrenGap: spacing.m }} wrap grow disableShrink>
            {itemList.map((i) => (
              <Tile key={i[`@id`]} item={i} />
            ))}
          </Stack>
        )}
    </Card>
  );
});

function getListByType(type: EntityType, item: IItem): IItem[] {
  let items: IItem[];
  if (type === EntityType.Company) {
    items = (item as ICompany).contacts ?? [];
  } else {
    items = (item as IProject).companies ?? [];
  }

  return items.slice().sort(sortItemByBooleanDesc(`@isRelevant`));
}

function getTileByType(type: EntityType): typeof CompanyTile | typeof ContactTile {
  if (type === EntityType.Company) {
    return ContactTile;
  }

  return CompanyTile;
}
